import React, { useState, useReducer, useEffect } from 'react';
import { useRooms } from '../../context/RoomContext';
import { Edit, Trash2, Plus, Check, X, Users, AlertCircle } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';

// Types
interface Room {
  id: string;
  name: string;
  description: string;
  price: number;
  capacity: number;
  amenities: string[];
  image_url: string;
  available: boolean;
}

interface RoomFormData {
  name: string;
  description: string;
  price: number;
  capacity: number;
  amenities: string[];
  image_url: string;
  available: boolean;
}

interface FormErrors {
  name?: string;
  description?: string;
  price?: string;
  capacity?: string;
  image_url?: string;
}

// Form state reducer
interface FormState {
  data: RoomFormData;
  errors: FormErrors;
  isSubmitting: boolean;
  isDirty: boolean;
}

type FormAction =
  | { type: 'UPDATE_FIELD'; field: keyof RoomFormData; value: any }
  | { type: 'SET_ERRORS'; errors: FormErrors }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | { type: 'RESET_FORM'; initialData?: Partial<RoomFormData> }
  | { type: 'ADD_AMENITY'; amenity: string }
  | { type: 'REMOVE_AMENITY'; index: number }
  | { type: 'SET_DIRTY'; isDirty: boolean };

const initialFormData: RoomFormData = {
  name: '',
  description: '',
  price: 0,
  capacity: 1,
  amenities: ['Wi-Fi'],
  image_url: '',
  available: true,
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return {
        ...state,
        data: { ...state.data, [action.field]: action.field === 'price' || action.field === 'capacity' ? Number(action.value) : action.value },
        errors: { ...state.errors, [action.field]: undefined },
        isDirty: true
      };
    case 'SET_ERRORS':
      return { ...state, errors: action.errors };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.isSubmitting };
    case 'RESET_FORM':
      return {
        data: { ...initialFormData, ...action.initialData },
        errors: {},
        isSubmitting: false,
        isDirty: false
      };
    case 'ADD_AMENITY':
      return {
        ...state,
        data: {
          ...state.data,
          amenities: [...state.data.amenities, action.amenity]
        },
        isDirty: true
      };
    case 'REMOVE_AMENITY':
      return {
        ...state,
        data: {
          ...state.data,
          amenities: state.data.amenities.filter((_, i) => i !== action.index)
        },
        isDirty: true
      };
    case 'SET_DIRTY':
      return { ...state, isDirty: action.isDirty };
    default:
      return state;
  }
};

// Validation functions
const validateForm = (data: RoomFormData): FormErrors => {
  const errors: FormErrors = {};

  if (!data.name.trim()) {
    errors.name = 'Room name is required';
  } else if (data.name.length < 2) {
    errors.name = 'Room name must be at least 2 characters';
  }

  if (!data.description.trim()) {
    errors.description = 'Description is required';
  } else if (data.description.length < 10) {
    errors.description = 'Description must be at least 10 characters';
  }

  if (data.price <= 0) {
    errors.price = 'Price must be greater than 0';
  }

  if (data.capacity < 1) {
    errors.capacity = 'Capacity must be at least 1';
  }

  if (!data.image_url) {
    errors.image_url = 'Room image is required';
  }

  return errors;
};

// Form component for reusability
interface RoomFormProps {
  formState: FormState;
  dispatch: React.Dispatch<FormAction>;
  onSubmit: () => void;
  onCancel: () => void;
  isEditing?: boolean;
  amenityInput: string;
  setAmenityInput: (value: string) => void;
}

const RoomForm: React.FC<RoomFormProps> = ({
  formState,
  dispatch,
  onSubmit,
  onCancel,
  isEditing = false,
  amenityInput,
  setAmenityInput
}) => {
  const { data, errors, isSubmitting, isDirty } = formState;

  // Validate form on mount and when data changes
  useEffect(() => {
    const errors = validateForm(data);
    dispatch({ type: 'SET_ERRORS', errors });
  }, [data, dispatch]);

  const handleAddAmenity = () => {
    if (amenityInput.trim()) {
      dispatch({ type: 'ADD_AMENITY', amenity: amenityInput.trim() });
      setAmenityInput('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAmenity();
    }
  };

  const isFormValid = Object.keys(errors).length === 0 && isDirty;

  return (
    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
      {/* Room Name */}
      <div className="sm:col-span-3">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Room Name *
        </label>
        <div className="mt-1">
          <input
            type="text"
            name="name"
            id="name"
            value={data.name}
            onChange={(e) => dispatch({ type: 'UPDATE_FIELD', field: 'name', value: e.target.value })}
            className={`form-input ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {errors.name && (
            <p id="name-error\" className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.name}
            </p>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="sm:col-span-3">
        <label htmlFor="price" className="block text-sm font-medium text-gray-700">
          Price per Night ($) *
        </label>
        <div className="mt-1">
          <input
            type="number"
            name="price"
            id="price"
            min="0"
            step="0.01"
            value={data.price}
            onChange={(e) => dispatch({ type: 'UPDATE_FIELD', field: 'price', value: e.target.value })}
            className={`form-input ${errors.price ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            aria-invalid={!!errors.price}
            aria-describedby={errors.price ? 'price-error' : undefined}
          />
          {errors.price && (
            <p id="price-error\" className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.price}
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="sm:col-span-6">
        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
          Description *
        </label>
        <div className="mt-1">
          <textarea
            id="description"
            name="description"
            rows={3}
            value={data.description}
            onChange={(e) => dispatch({ type: 'UPDATE_FIELD', field: 'description', value: e.target.value })}
            className={`form-input min-h-[100px] ${errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            aria-invalid={!!errors.description}
            aria-describedby={errors.description ? 'description-error' : undefined}
          />
          {errors.description && (
            <p id="description-error\" className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.description}
            </p>
          )}
        </div>
      </div>

      {/* Capacity */}
      <div className="sm:col-span-3">
        <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
          Capacity *
        </label>
        <div className="mt-1">
          <input
            type="number"
            name="capacity"
            id="capacity"
            min="1"
            value={data.capacity}
            onChange={(e) => dispatch({ type: 'UPDATE_FIELD', field: 'capacity', value: e.target.value })}
            className={`form-input ${errors.capacity ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            aria-invalid={!!errors.capacity}
            aria-describedby={errors.capacity ? 'capacity-error' : undefined}
          />
          {errors.capacity && (
            <p id="capacity-error\" className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.capacity}
            </p>
          )}
        </div>
      </div>

      {/* Availability */}
      <div className="sm:col-span-3">
        <label htmlFor="available" className="block text-sm font-medium text-gray-700">
          Availability
        </label>
        <div className="mt-1">
          <select
            id="available"
            name="available"
            value={data.available ? 'true' : 'false'}
            onChange={(e) => dispatch({ type: 'UPDATE_FIELD', field: 'available', value: e.target.value === 'true' })}
            className="form-select"
          >
            <option value="true">Available</option>
            <option value="false">Not Available</option>
          </select>
        </div>
      </div>

      {/* Room Image */}
      <div className="sm:col-span-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Room Image *
        </label>
        <ImageUpload
          currentImage={data.image_url}
          onImageChange={(imageData) => dispatch({ type: 'UPDATE_FIELD', field: 'image_url', value: imageData })}
        />
        {errors.image_url && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {errors.image_url}
          </p>
        )}
      </div>

      {/* Amenities */}
      <div className="sm:col-span-6">
        <label htmlFor="amenities" className="block text-sm font-medium text-gray-700">
          Amenities
        </label>
        <div className="mt-1 flex">
          <input
            type="text"
            name="amenities"
            id="amenities"
            value={amenityInput}
            onChange={(e) => setAmenityInput(e.target.value)}
            onKeyPress={handleKeyPress}
            className="form-input"
            placeholder="Add an amenity"
          />
          <button
            type="button"
            onClick={handleAddAmenity}
            disabled={!amenityInput.trim()}
            className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2" role="list" aria-label="Room amenities">
          {data.amenities.map((amenity, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-indigo-100 text-indigo-800"
              role="listitem"
            >
              {amenity}
              <button
                type="button"
                onClick={() => dispatch({ type: 'REMOVE_AMENITY', index })}
                className="ml-1.5 inline-flex text-indigo-400 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                aria-label={`Remove ${amenity} amenity`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Form Actions */}
      <div className="sm:col-span-6 flex justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!isFormValid || isSubmitting}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {isEditing ? 'Updating...' : 'Saving...'}
            </>
          ) : (
            isEditing ? 'Update Room' : 'Save Room'
          )}
        </button>
      </div>
    </div>
  );
};

// Main component
const AdminRooms: React.FC = () => {
  const { rooms, addRoom, updateRoom, deleteRoom } = useRooms();
  
  // UI State
  const [isAddingRoom, setIsAddingRoom] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(null);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  
  // Form State
  const [addFormState, addFormDispatch] = useReducer(formReducer, {
    data: initialFormData,
    errors: {},
    isSubmitting: false,
    isDirty: false
  });
  
  const [editFormState, editFormDispatch] = useReducer(formReducer, {
    data: initialFormData,
    errors: {},
    isSubmitting: false,
    isDirty: false
  });
  
  // Amenity inputs
  const [newAmenity, setNewAmenity] = useState('');
  const [editAmenity, setEditAmenity] = useState('');

  // Show notification
  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Handle add room
  const handleAddRoom = async () => {
    const errors = validateForm(addFormState.data);
    
    if (Object.keys(errors).length > 0) {
      addFormDispatch({ type: 'SET_ERRORS', errors });
      return;
    }

    addFormDispatch({ type: 'SET_SUBMITTING', isSubmitting: true });
    
    try {
      await addRoom(addFormState.data);
      showNotification('success', 'Room added successfully!');
      addFormDispatch({ type: 'RESET_FORM' });
      setIsAddingRoom(false);
      setNewAmenity('');
    } catch (error) {
      console.error('Error adding room:', error);
      showNotification('error', 'Failed to add room. Please try again.');
    } finally {
      addFormDispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
    }
  };

  // Handle edit room
  const handleEditRoom = async () => {
    if (!editingRoomId) return;
    
    const errors = validateForm(editFormState.data);
    
    if (Object.keys(errors).length > 0) {
      editFormDispatch({ type: 'SET_ERRORS', errors });
      return;
    }

    editFormDispatch({ type: 'SET_SUBMITTING', isSubmitting: true });
    
    try {
      await updateRoom(editingRoomId, editFormState.data);
      showNotification('success', 'Room updated successfully!');
      setEditingRoomId(null);
      setEditAmenity('');
    } catch (error) {
      console.error('Error updating room:', error);
      showNotification('error', 'Failed to update room. Please try again.');
    } finally {
      editFormDispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
    }
  };

  // Start editing
  const startEditing = (room: Room) => {
    editFormDispatch({ type: 'RESET_FORM', initialData: room });
    setEditingRoomId(room.id);
    setEditAmenity('');
  };

  // Handle delete
  const handleDeleteClick = (id: string) => {
    setDeleteConfirmation(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmation) return;
    
    try {
      await deleteRoom(deleteConfirmation);
      showNotification('success', 'Room deleted successfully!');
      setDeleteConfirmation(null);
    } catch (error) {
      console.error('Error deleting room:', error);
      showNotification('error', 'Failed to delete room. Please try again.');
    }
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Manage Rooms</h2>
        <button
          onClick={() => setIsAddingRoom(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Room
        </button>
      </div>

      {/* Notifications */}
      {notification && (
        <div className={`mb-4 p-4 rounded-md ${
          notification.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          <p className="text-sm">{notification.message}</p>
        </div>
      )}

      {/* Add Room Form */}
      {isAddingRoom && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-5">Add New Room</h3>
            <RoomForm
              formState={addFormState}
              dispatch={addFormDispatch}
              onSubmit={handleAddRoom}
              onCancel={() => {
                setIsAddingRoom(false);
                addFormDispatch({ type: 'RESET_FORM' });
                setNewAmenity('');
              }}
              amenityInput={newAmenity}
              setAmenityInput={setNewAmenity}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setDeleteConfirmation(null)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <Trash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Delete Room
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this room? This action cannot be undone and will remove all associated data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteConfirmation(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rooms List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {rooms.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No rooms</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new room.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {rooms.map((room) => (
              <li key={room.id}>
                {editingRoomId === room.id ? (
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-5">Edit Room</h3>
                    <RoomForm
                      formState={editFormState}
                      dispatch={editFormDispatch}
                      onSubmit={handleEditRoom}
                      onCancel={() => {
                        setEditingRoomId(null);
                        setEditAmenity('');
                      }}
                      isEditing={true}
                      amenityInput={editAmenity}
                      setAmenityInput={setEditAmenity}
                    />
                  </div>
                ) : (
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-12 w-12">
                          <img
                            className="h-12 w-12 rounded-lg object-cover"
                            src={room.image_url}
                            alt={room.name}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-indigo-600">{room.name}</div>
                          <div className="text-sm text-gray-500">${room.price} per night</div>
                          <div className="text-xs text-gray-400 mt-1 max-w-md truncate">
                            {room.description}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => startEditing(room)}
                          className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          aria-label={`Edit ${room.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(room.id)}
                          className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          aria-label={`Delete ${room.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 sm:flex sm:justify-between">
                      <div className="sm:flex sm:space-x-6">
                        <p className="flex items-center text-sm text-gray-500">
                          <Users className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          Capacity: {room.capacity}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <span className={`flex-shrink-0 mr-1.5 h-2 w-2 rounded-full ${room.available ? 'bg-green-400' : 'bg-red-400'}`} />
                          {room.available ? 'Available' : 'Not Available'}
                        </p>
                      </div>
                      <div className="mt-2 sm:mt-0">
                        <div className="flex flex-wrap gap-1">
                          {room.amenities.slice(0, 3).map((amenity, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {amenity}
                            </span>
                          ))}
                          {room.amenities.length > 3 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                              +{room.amenities.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminRooms;